/**
 * ULESTE-TALLET PÅ ET MENYPUNKT (KOI Del 4: «sidemenyen viser uleste-tall på Innboks og Oppgaver»).
 *
 * =================================================================================================
 * KOMPONENTEN VET HVILKE PUNKTER SOM HAR ET TALL, OG MENYEN VET DET IKKE.
 *
 * Alternativet var et felt på `NavEntry` — `unreadKey: 'inbox' | 'tasks'` — og det ble avvist: en
 * meny er en LISTE OVER STEDER, og et felt der ville gjort hver ny meny-oppføring til et sted man
 * må ta stilling til uleste. Her er det to id-er i en tabell, og den som legger til et tredje tall
 * ser hvor de to andre bor.
 *
 * `navigation.ts` er dessuten append-only og delt mellom flere agenter (CLAUDE.md); en ny kolonne
 * der er en fil flere kan røre samtidig.
 *
 * =================================================================================================
 * INGEN TALL, INGEN PLASS. En null skrives ikke som «0» — den tegnes ikke. Et menypunkt med et
 * grått null-merke ser ut som noe man har oversett, og det er det motsatte av hva et merke er til.
 *
 * TALLET ER ET ANTALL SAMTALER, IKKE MELDINGER, på Innboks. Det står i ruten (`unreadCounts`) og
 * er den samme tellingen `hasUnread` bygger på — «du har tre samtaler å se på». Oppgavetallet er
 * mine åpne som forfaller i dag eller er forfalt, som er ordrens egen definisjon.
 */
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { meQueryOptions } from '@/api/queries/me';
import { formatNumber } from '@/lib/format';

import { inboxUnreadCountsQueryOptions } from './queries';

/** Menypunktene som har et tall, og hvilket av de to tallene de viser. */
const COUNTED: Readonly<Record<string, 'inbox' | 'tasks'>> = {
  inbox: 'inbox',
  tasks: 'tasks',
};

export function UnreadBadge({ navId }: { navId: string }): ReactNode {
  const which = COUNTED[navId];

  /*
   * `useQuery` OG IKKE `useMe()`, OG DET VAR SEKS RØDE SKALLTESTER SOM SA DET.
   *
   * `useMe()` er en SUSPENSE-spørring: den kaster en promise når identiteten ikke er lastet, og
   * her sitter den inne i hver eneste menyrad. Skalltestene tegner menyen med en stubbet `me` uten
   * en spørring bak, og fikk «Cannot read properties of undefined».
   *
   * Det er ikke bare en testsak. Et MERKE er pynt: det skal aldri være grunnen til at menyen ikke
   * tegnes. Med den ikke-suspenderende formen står raden med navn og ikon uansett, og tallet
   * kommer når det kommer.
   */
  const me = useQuery(meQueryOptions()).data;
  const organizationId = me?.activeOrganizationId ?? null;

  const counts = useQuery({
    ...inboxUnreadCountsQueryOptions(organizationId ?? ''),
    /*
     * Spørringen finnes bare for de to punktene, og bare i et hus. React Query deler nøkkelen,
     * så to merker er én henting — og `enabled` gjør at resten av menyen ikke ber om noe.
     */
    enabled: which !== undefined && organizationId !== null,
  });

  if (which === undefined || me === undefined) return null;
  const count = which === 'inbox' ? (counts.data?.inbox ?? 0) : (counts.data?.tasks ?? 0);
  if (count === 0) return null;

  return (
    <span
      data-testid={`nav-unread-${navId}`}
      className="numeric min-w-4 shrink-0 rounded-full bg-accent-strong px-1 text-center text-2xs leading-4 font-medium text-on-accent"
    >
      {formatNumber(count, me.user.locale)}
    </span>
  );
}
